import React, { useEffect, useRef, useState } from 'react';
import { Spin, message, DatePicker, Select, Space } from 'antd';
import * as echarts from 'echarts';
import dayjs, { Dayjs } from 'dayjs';
import { getLeadIntentionDistribution, LeadIntentionDistributionData } from '../../../services/statistics';
import { getFilterOptions, FilterOptions } from '../../../services/lead';
import { getAssignableUsers, UserAssignee } from '../../../services/user';

const { RangePicker } = DatePicker;
const { Option } = Select;

const LeadIntentionDistributionChart: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<LeadIntentionDistributionData | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    data_sources: [],
    provinces: [],
    cities: [],
    districts: [],
    urgency_levels: [],
    follow_statuses: [],
    lead_types: [],
  });
  const [users, setUsers] = useState<UserAssignee[]>([]);
  
  // 筛选条件
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [assignedTo, setAssignedTo] = useState<number | undefined>(undefined);
  const [dataSource, setDataSource] = useState<string | undefined>(undefined);
  const [leadType, setLeadType] = useState<string | undefined>(undefined);

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      
      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      if (assignedTo) params.assigned_to = assignedTo;
      if (dataSource) params.data_source = dataSource;
      if (leadType) params.lead_type = leadType;
      
      const data = await getLeadIntentionDistribution(params);
      
      // 验证数据格式
      if (!data || !Array.isArray(data.employees) || !Array.isArray(data.intentions) || !Array.isArray(data.data)) {
        console.error('数据格式错误:', data);
        message.error('数据格式错误');
        setChartData(null);
        return;
      }
      
      setChartData(data);
    } catch (error) {
      console.error('Failed to load lead intention distribution data:', error);
      message.error('加载数据失败');
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  // 加载筛选选项
  const loadFilterOptions = async () => {
    try {
      const options = await getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  // 加载用户列表
  const loadUsers = async () => {
    try {
      const usersData = await getAssignableUsers({ limit: 100 });
      setUsers(usersData || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // 初始加载
  useEffect(() => {
    loadFilterOptions();
    loadUsers();
    loadData();
  }, []);

  // 筛选条件变化时重新加载
  useEffect(() => {
    loadData();
  }, [dateRange, assignedTo, dataSource, leadType]);

  // 渲染图表
  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // 如果没有数据，只保留横坐标，清空系列
    if (!chartData || chartData.employees.length === 0 || chartData.intentions.length === 0) {
      const emptyOption = {
        tooltip: {
          show: false,
        },
        legend: {
          data: [],
          bottom: 0,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            show: true,
          },
          axisLine: {
            show: true,
          },
        },
        yAxis: {
          type: 'value',
          show: false,
        },
        series: [],
      };
      chartInstance.current.setOption(emptyOption, true);
      return;
    }

    // 定义跟进意向的颜色映射（根据常见意向关键词匹配）
    // 使用Map记录已分配的颜色，确保不重复
    const colorMap = new Map<string, string>();
    // 使用 Ant Design 标准色系（扩展版，支持更多意向）
    const availableColors = [
      '#1890ff', // 蓝色 - 中意向（主色调，Ant Design Primary）
      '#52c41a', // 绿色 - 高意向（积极，Success）
      '#faad14', // 橙色 - 低意向（警示，Warning）
      '#f5222d', // 红色 - 无意向（消极，Error）
      '#722ed1', // 紫色 - 待跟进（中性）
      '#13c2c2', // 青色 - Cyan
      '#eb2f96', // 粉色 - Magenta
      '#fa8c16', // 深橙 - Orange
      '#a0d911', // 黄绿 - Lime
      '#2f54eb', // 深蓝 - Geek Blue
      '#fa541c', // 火红 - Volcano
      '#597ef7', // 靛蓝 - Blue
      // 扩展颜色（支持更多意向）
      '#73d13d', // 浅绿 - Green
      '#ff7a45', // 珊瑚橙 - Coral
      '#9254de', // 浅紫 - Purple
      '#40a9ff', // 天蓝 - Sky Blue
      '#ffc53d', // 金黄 - Gold
      '#ff85c0', // 浅粉 - Pink
      '#36cfc9', // 青绿 - Teal
      '#b37feb', // 淡紫 - Lavender
      '#ff9c6e', // 杏色 - Apricot
      '#95de64', // 草绿 - Grass Green
      '#5cdbd3', // 薄荷绿 - Mint
      '#85a5ff', // 浅蓝 - Light Blue
    ];
    let colorIndex = 0;
    
    const getIntentionColor = (intention: string): string => {
      // 如果已经分配过颜色，直接返回
      if (colorMap.has(intention)) {
        return colorMap.get(intention)!;
      }
      
      const intentionLower = intention.toLowerCase();
      let color: string;
      
      // 根据关键词匹配优先颜色
      if (intentionLower.includes('高') || intentionLower.includes('很有') || intentionLower.includes('强')) {
        color = availableColors[1]; // 绿色 - 高意向（Success）
      } else if (intentionLower.includes('中') || intentionLower.includes('有意') || intentionLower.includes('一般')) {
        color = availableColors[0]; // 蓝色 - 中意向（Primary）
      } else if (intentionLower.includes('低') || intentionLower.includes('较低')) {
        color = availableColors[2]; // 橙色 - 低意向（Warning）
      } else if (intentionLower.includes('无') || intentionLower.includes('没有')) {
        color = availableColors[3]; // 红色 - 无意向（Error）
      } else if (intentionLower.includes('待') || intentionLower.includes('未')) {
        color = availableColors[4]; // 紫色 - 待跟进
      } else {
        // 使用未分配的颜色（从第5个开始）
        const startIndex = 5;
        color = availableColors[(startIndex + colorIndex) % availableColors.length];
        colorIndex++;
      }
      
      // 如果颜色已被使用，找下一个可用颜色（添加最大循环次数防止死循环）
      let attempts = 0;
      const maxAttempts = availableColors.length;
      while (Array.from(colorMap.values()).includes(color) && attempts < maxAttempts) {
        colorIndex++;
        color = availableColors[colorIndex % availableColors.length];
        attempts++;
      }
      
      // 如果所有颜色都被使用了，就重复使用（静默处理）
      
      colorMap.set(intention, color);
      return color;
    };

    // 构建系列数据
    let series;
    try {
      series = chartData.intentions.map((intention) => {
        const seriesData = chartData.employees.map((_, empIndex) => {
          const empData = chartData.data[empIndex];
          const value = empData && empData[intention] ? empData[intention] : 0;
          return value;
        });
        
        return {
          name: intention,
          type: 'bar',
          stack: 'total',
          data: seriesData,
          itemStyle: {
            color: getIntentionColor(intention),
          },
        };
      });
    } catch (error) {
      console.error('构建系列数据时出错:', error);
      return;
    }

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          let result = `${params[0].axisValue}<br/>`;
          let total = 0;
          params.forEach((item: any) => {
            if (item.value > 0) {
              result += `${item.marker}${item.seriesName}: ${item.value}<br/>`;
              total += item.value;
            }
          });
          result += `<strong>总计: ${total}</strong>`;
          return result;
        },
      },
      legend: {
        data: chartData.intentions,
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: chartData.employees,
        axisLabel: {
          interval: 0,
          rotate: chartData.employees.length > 5 ? 30 : 0,
        },
      },
      yAxis: {
        type: 'value',
        name: '线索数量',
        minInterval: 1,
      },
      series: series,
    };

    try {
      chartInstance.current.setOption(option, true); // 使用 true 参数完全替换配置
    } catch (error) {
      console.error('线索意向分布图表渲染失败:', error);
    }

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartData]);

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="small">
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
            placeholder={['认领开始日期', '认领结束日期']}
            style={{ width: 240 }}
          />
          <Select
            placeholder="负责人"
            allowClear
            style={{ width: 120 }}
            value={assignedTo}
            onChange={setAssignedTo}
            showSearch
            optionFilterProp="children"
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>
                {user.real_name || `用户${user.id}`}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="数据来源"
            allowClear
            style={{ width: 120 }}
            value={dataSource}
            onChange={setDataSource}
            showSearch
            optionFilterProp="children"
          >
            {filterOptions.data_sources.map(source => (
              <Option key={source} value={source}>
                {source}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="线索类型"
            allowClear
            style={{ width: 120 }}
            value={leadType}
            onChange={setLeadType}
            showSearch
            optionFilterProp="children"
          >
            {filterOptions.lead_types.map(type => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Space>
      </Space>
      <div ref={chartRef} style={{ width: '100%', height: '300px' }} />
    </Spin>
  );
};

export default LeadIntentionDistributionChart;
